import { useEffect, useRef, useState } from 'react';

import { capitalize, getWindowDimensions, isMobileOrTablet } from 'App/Helpers';

let initialOrientationTracked = false;

/**
 * Returns 'portrait' or 'landscape' in a cross-browser, safe way.
 *
 * @param {Event} [event] — optional orientation change event
 * @returns {'portrait' | 'landscape'}
 */
function getDeviceOrientation(event) {
    if (typeof window === 'undefined') return null;

    // Helper to normalize any type → 'portrait' | 'landscape'
    const normalize = (val) =>
        val && val.toString().startsWith('portrait') ? 'portrait' : 'landscape';

    // From event (modern browsers often provide event.target.type)
    const fromEvent = event?.target?.type;
    if (fromEvent) return normalize(fromEvent);

    // Modern Screen Orientation API
    const fromScreen = window.screen?.orientation?.type;
    if (fromScreen) return normalize(fromScreen);

    // Legacy window.orientation (mainly old mobile Safari/Android)
    if (typeof window.orientation === 'number') {
        return window.orientation === 0 || window.orientation === 180 ? 'portrait' : 'landscape';
    }

    // Fallback: viewport dimensions
    const getDimensions = () => {
        let w = window.innerWidth ?? document.documentElement?.clientWidth ?? window.screen?.width;
        let h =
            window.innerHeight ?? document.documentElement?.clientHeight ?? window.screen?.height;
        return { w, h };
    };

    const { w, h } = getDimensions();

    if (!w || !h) return 'landscape'; // ultimate fallback

    return h >= w ? 'portrait' : 'landscape';
}

/**
 * hook watches screen orientation changes
 *
 * @param {Function} sendGameDesignEvent - Deferred GameAnalytics design event.
 * @param {Function} sendGoogleBasicEvent - Deferred Google Analytics basic event.
 *
 * @return orientation | blockGameRender | orientationMessage | isMobileDevice
 */
export const useDeviceOrientation = ({ sendGameDesignEvent, sendGoogleBasicEvent }) => {
    const [{ initialOrientation, isMobileDevice }] = useState(() => ({
        initialOrientation: getDeviceOrientation(),
        isMobileDevice: isMobileOrTablet(),
    }));
    const [orientation, setOrientation] = useState(initialOrientation);
    const [blockGameRender, setBlockGameRender] = useState(
        isMobileDevice && initialOrientation !== 'portrait'
    );
    const [orientationMessage, setOrientationMessage] = useState(false);

    const orientationRef = useRef(initialOrientation);
    const analyticsRef = useRef({
        sendGameDesignEvent,
        sendGoogleBasicEvent,
    });

    analyticsRef.current = { sendGameDesignEvent, sendGoogleBasicEvent };

    const trackOrientation = (nextOrientation) => {
        analyticsRef.current.sendGameDesignEvent(
            `Game:ScreenOrientation:${capitalize(nextOrientation)}`
        );
        analyticsRef.current.sendGoogleBasicEvent('game', `screen_orientation_${nextOrientation}`);
    };

    // Track the orientation present at page load only once, even if the hook
    // remounts while the user stays on the same page. The deferred senders
    // queue the event until the analytics clients are available.
    useEffect(() => {
        if (!initialOrientation || initialOrientationTracked) return;

        initialOrientationTracked = true;
        trackOrientation(initialOrientation);
    }, []);

    // set screen orientation listener if device is mobile type
    useEffect(() => {
        if (!isMobileDevice) return;

        const handleOrientationChange = (event) => {
            const newOrientation = getDeviceOrientation(event);

            if (!newOrientation || newOrientation === orientationRef.current) return;

            orientationRef.current = newOrientation;
            trackOrientation(newOrientation);
            setOrientation(newOrientation);
        };

        // Prefer modern Screen Orientation API if available
        const orientationObj = window.screen?.orientation;

        if (orientationObj?.addEventListener) {
            orientationObj.addEventListener('change', handleOrientationChange);

            return () => {
                orientationObj.removeEventListener('change', handleOrientationChange);
            };
        }

        // Fallback to legacy 'orientationchange' on window
        window.addEventListener('orientationchange', handleOrientationChange);

        return () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    // handle device orientation change
    useEffect(() => {
        if (!orientation) return;
        if (!isMobileDevice || orientation === 'portrait') {
            // Desktop or portrait: never block the game
            setBlockGameRender(false);
            setOrientationMessage(false);
            return;
        }

        const { height } = getWindowDimensions();

        document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
        window.scrollTo(0, 0);
        setOrientationMessage(true);

        analyticsRef.current.sendGameDesignEvent('Game:ScreenLandscapeWarning');
        analyticsRef.current.sendGoogleBasicEvent('game', 'screen_landscape_warning');
    }, [orientation]);

    return { orientation, blockGameRender, orientationMessage, isMobileDevice };
};
